import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';
import { Formik } from 'formik';
import { head } from 'lodash-es';

import StepNavigator from './StepNavigator';
import Step from './Step';
import { getStep, getStepIndex, getStepFromIndex } from './utils';
import {
  StepConfig,
  StepTouchedStatus,
  MultiStepFormValues,
  MultiStepFormErrors,
  MultiStepFormProps,
} from './types';

const StyledContainer = styled.div`
  display: flex;
  height: 100%;
`;

const StyledInnerContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const buildInitialValues = (steps: MultiStepFormProps['steps']) => {
  const values: MultiStepFormValues = {};

  steps.forEach(({ name, initialValues = {} }) => {
    values[name] = initialValues;
  });

  return values;
};

const buildNavigationItemConfig = (
  steps: MultiStepFormProps['steps'],
  values: MultiStepFormValues,
) => steps.map(({ name, navItemComponent, navContentComponent }) => ({
  name,
  navItemComponent,
  navContentComponent,
  values: values[name],
}));

const validateSteps = (
  values: MultiStepFormValues,
  steps: MultiStepFormProps['steps'],
) => {
  const errors: MultiStepFormErrors = {};

  steps.forEach(({ name, validator }) => {
    if (validator) {
      const stepError = validator(values[name]);

      if (stepError) {
        errors[name] = stepError;
      }
    }
  });

  return errors;
};

/**
 * Component to render a multi-step form.
 */
const MultiStepForm = ({
  steps,
  initialStep,
  onSubmit = () => {},
  onStepChange = () => {},
  navItemComponent,
  defaultBackButtonLabel,
  defaultNextButtonLabel,
  submitButtonLabel,
  renderBackButton,
  renderNextButton,
  renderSubmitButton,
  showNextButton,
  showBackButton,
  className,
  buttonContainerClassName,
  isSubmitting,
}: MultiStepFormProps) => {
  const [currentStep, setCurrentStep] = useState<StepConfig<any>>();

  useEffect(() => {
    const newStep = initialStep ? getStep(steps, initialStep) : head(steps);

    setCurrentStep(newStep);
  }, [JSON.stringify(steps), initialStep]);

  const currentStepIndex = useMemo(() => (
    getStepIndex(steps, currentStep?.name)
  ), [steps, currentStep]);

  const onNavigationItemClick = useCallback((stepName) => {
    setCurrentStep(getStep(steps, stepName));
  }, [steps]);

  const onNavigationButtonClick = useCallback((nextStepIndex) => {
    const nextStep = getStepFromIndex(steps, nextStepIndex);

    setCurrentStep(nextStep);
    onStepChange();
  }, [steps, onStepChange]);

  const validate = useCallback((values) => validateSteps(values, steps), [steps]);

  if (!currentStep) {
    return null;
  }

  return (
    <StyledContainer className={className}>
      <Formik
        initialValues={buildInitialValues(steps)}
        onSubmit={onSubmit}
        validate={validate}
      >
        {({ values, errors, touched }) => (
          <StyledInnerContainer>
            <StepNavigator
              currentStepIndex={currentStepIndex}
              onNavigationItemClick={onNavigationItemClick}
              itemConfig={buildNavigationItemConfig(steps, values)}
              navItemComponent={navItemComponent}
            />
            <Step
              currentStep={currentStep}
              currentStepIndex={currentStepIndex}
              numberOfSteps={steps.length}
              stepValues={values[currentStep.name]}
              errors={errors as MultiStepFormErrors}
              touched={touched[currentStep.name] as StepTouchedStatus}
              onBackButtonClick={onNavigationButtonClick}
              onNextButtonClick={onNavigationButtonClick}
              defaultBackButtonLabel={defaultBackButtonLabel}
              defaultNextButtonLabel={defaultNextButtonLabel}
              submitButtonLabel={submitButtonLabel}
              renderBackButton={renderBackButton}
              renderNextButton={renderNextButton}
              renderSubmitButton={renderSubmitButton}
              showNextButton={showNextButton}
              showBackButton={showBackButton}
              buttonContainerClassName={buttonContainerClassName}
              isSubmitting={isSubmitting}
            />
          </StyledInnerContainer>
        )}
      </Formik>
    </StyledContainer>
  );
};

export default MultiStepForm;
